import { computed, makeObservable, observable } from 'mobx';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';

import {
    EntryTypeFieldData,
    EntryTypeFieldName,
    EntryTypeFieldType,
    FieldDataParams,
    Nullable,
    Optional,
} from 'chaika-frontend-types/frontend-cms';
import { Formatter } from '@utils/formula-parser';
import type { FieldParams, FieldTranslationsData } from 'chaika-types';
import { Locale } from 'chaika-enums';

import { toArrayFromIterable } from '@utils/toArrayFromIterable';

export interface FieldData extends FieldDataParams {}

const systemFieldTypes = [
    EntryTypeFieldType.Allergy,
    EntryTypeFieldType.DrugAllergy,
    EntryTypeFieldType.Pregnancy,
    EntryTypeFieldType.MedicalCases,
    EntryTypeFieldType.Drug,
    EntryTypeFieldType.Referrals,
    EntryTypeFieldType.LaboratoryTests,
    EntryTypeFieldType.DentalTreatment,
    EntryTypeFieldType.EpisdodesPlanDescription,
];

export const systemFieldTypesSet = new Set(systemFieldTypes);

const FIELD_NAME_REG = /[^A-Za-z0-9 _-]+/;

const typeFieldsWithTranslation: EntryTypeFieldType[] = [
    EntryTypeFieldType.Text,
    EntryTypeFieldType.UsedMedications,
    EntryTypeFieldType.EpisdodesDrugs,
    EntryTypeFieldType.EpisdodesReferrals,
    EntryTypeFieldType.EpisdodesRecommendations,
    EntryTypeFieldType.EpisdodesTasks,
];

const fieldsHiddenWithoutDate: (EntryTypeFieldName | string)[] = [EntryTypeFieldName.ConclusionText];

export class EntryTypeField {
    public static isFiledSystem(type: string): boolean {
        return systemFieldTypes.includes(type as EntryTypeFieldType);
    }

    @observable public readonly id: string;
    @observable public title: Optional<string>;
    @observable public type: EntryTypeFieldType;
    @observable public placeholder: Optional<string>;
    @observable public dictionary: Optional<string>;
    @observable public global: boolean;
    @observable public titleTop: boolean;
    @observable public titleTranslations = new Map<Locale, FieldTranslationsData>();
    @observable public width: Optional<number>;
    @observable public formula?: Nullable<string>;
    @observable public data: FieldData;

    @observable protected fieldName: string | EntryTypeFieldName;

    protected formatter: Formatter;

    constructor({
        name,
        title,
        type,
        placeholder,
        dictionary,
        global = false,
        width,
        titleTop = true,
        formula,
        data,
        titleTranslations = [],
    }: EntryTypeFieldData) {
        makeObservable(this);

        this.id = name;
        this.fieldName = name;
        this.title = title;
        this.type = type;
        this.placeholder = placeholder;
        this.dictionary = dictionary;
        this.global = global;
        this.titleTop = titleTop;
        this.width = width;
        this.data = data || {};
        this.formatter = new Formatter(this.data.format);
        this.formula = this.getFixedFormula(type, formula);
        titleTranslations.forEach(title => this.titleTranslations.set(title.locale, title));
    }

    @computed
    public get name(): string {
        return this.fieldName;
    }

    @computed
    public get translationLocales(): string[] {
        return Array.from(this.titleTranslations.keys());
    }

    @computed
    public get titleTranslationsValues(): FieldTranslationsData[] {
        return toArrayFromIterable(this.titleTranslations);
    }

    @computed
    public get isSystem(): boolean {
        return systemFieldTypes.includes(this.type);
    }

    @computed
    public get includeInHistory(): boolean {
        return (
            this.global &&
            this.type !== EntryTypeFieldType.Date &&
            this.type !== EntryTypeFieldType.String &&
            this.type !== EntryTypeFieldType.Number &&
            this.type !== EntryTypeFieldType.Computed
        );
    }

    @computed
    public get saveParams(): FieldParams {
        return omitBy<EntryTypeFieldData>(
            {
                name: this.name,
                type: this.type,
                data: this.data,
                global: this.global,
                title: this.title,
                titleTop: this.titleTop,
                placeholder: this.placeholder,
                width: this.width,
                dictionary: this.dictionary,
                formula: this.getFixedFormula(this.type, this.formula),
                titleTranslations: this.titleTranslationsValues,
            },
            isNil,
        ) as unknown as FieldParams;
    }

    @computed
    public get hasTranslationMode(): boolean {
        return typeFieldsWithTranslation.includes(this.type);
    }

    @computed
    public get hideWithoutData(): boolean {
        return fieldsHiddenWithoutDate.includes(this.name);
    }

    public set name(name: string) {
        if (!FIELD_NAME_REG.test(name)) {
            this.fieldName = name;
        }
    }

    public format(value: string): string {
        if (this.fieldName === EntryTypeFieldName.DayOfMenstrualCycle && !value) {
            return value;
        }

        return this.formatter.format(value);
    }

    public getFixedFormula(
        type: EntryTypeFieldType,
        formula?: Nullable<string> | undefined,
    ): Nullable<string | undefined> {
        if (!!formula && type !== EntryTypeFieldType.Computed && type !== EntryTypeFieldType.CustomComputed) {
            return undefined;
        }

        return formula;
    }
}
